import z from "zod/v4";

import { Edge, Node } from "@xyflow/react";

import { NodeType } from "./nodes/base-node";
import {
  NodeDefinition,
  NodeDefinitionSchema,
  NodeEdgeSchema,
} from "./nodes/node-definition";
import { LanguageModelSchema } from "./common";
import { AiNodeContext, AiNodeContextData } from "./context-schemas";
import { ConversationAction } from "./conversation-state";
import { BaseNodeExecutionResult } from "./node-execution-result";
import { SessionStatus } from "./session";
import { ContextConfigSchema } from "./unified-context";

export const WorkflowConfigSchema = z.object({
  defaultModel: LanguageModelSchema,
  description: z.string().optional(),
  maxSessionDuration: z.number().optional(),
  defaultTimeout: z.number().optional(),
});

export type WorkflowConfig = z.infer<typeof WorkflowConfigSchema>;

export const BaseWorkflowDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  version: z.string(),
  viewport: z
    .object({ x: z.number(), y: z.number(), zoom: z.number() })
    .optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.number().optional(),
  updatedAt: z.number().optional(),
  contextConfig: ContextConfigSchema.optional(),
  config: WorkflowConfigSchema.optional(),
  production: z.boolean().optional(),
});

export type BaseWorkflowDefinition = z.infer<
  typeof BaseWorkflowDefinitionSchema
>;

export const WorkflowDefinitionSchema = BaseWorkflowDefinitionSchema.extend({
  nodes: z.array(NodeDefinitionSchema),
  edges: z.array(NodeEdgeSchema),
  contextConfig: ContextConfigSchema.optional(),
});

export type WorkflowDefinition = z.infer<typeof WorkflowDefinitionSchema>;

export interface WorkflowNodeData {
  nodeDefinition: NodeDefinition;
  isValid: boolean;
  validationErrors: string[];
  executionStatus?: SessionStatus;
  executionDuration?: number;
  executionError?: string;
  [key: string]: any;
}

export type UiWorkflowNode = Node<WorkflowNodeData>;
export type UiWorkflowEdge = Edge;

export type UiWorkflowDefinition = Omit<
  WorkflowDefinition,
  "nodes" | "edges"
> & {
  nodes: UiWorkflowNode[];
  edges: UiWorkflowEdge[];
};

export type CreateUiWorkflowDefinition = Omit<
  UiWorkflowDefinition,
  "createdAt" | "updatedAt"
>;

export type UiWorkflowSummary = Pick<
  UiWorkflowDefinition,
  "id" | "name" | "version"
> & { versions: { updatedAt: number; version: string }[] };

export interface StreamHandler {
  push(part: ConversationAction): void;
}

export interface AiNode {
  id: string;
  type: NodeType;
  name: string;
  workflowLogger: any;
  description?: string;

  execute(
    streamHandler: StreamHandler,
    context: AiNodeContext,
    contextData: AiNodeContextData,
    messageId: string
  ): Promise<BaseNodeExecutionResult>;

  setWorkflow(workflow: WorkflowDefinition): void;
}

export type ComputedWorkflow = {
  definition: WorkflowDefinition;
  nodes: Map<string, AiNode>;
};
